import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { isUserAdminInSociety } from '../../../../../../../helpers/society'
import { useAppTranslation } from '../../../../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../../../../hooks/useCurrentSociety'
import { useModal } from '../../../../../../../hooks/useModal'
import { useToastNotifications } from '../../../../../../../hooks/useToastNotification'
import { NSocietiesWebsites } from '../../../../../../../interfaces/services/societies-websites.interfaces'
import { Button, ButtonVariant } from '../../../../../../common/Button'
import { ConfirmationModal } from '../../../../../../common/ConfirmationModal'
import { ToastType } from '../../../../../../common/Toast/toast-type'
import { CreateUpdateSocietyWebsiteFAQItemModal } from '../CreateUpdateSocietyWebsiteFAQItemModal'
import { ISocietyWebsiteModel } from '../../../../../../../interfaces/models/societies-websites.interfaces'
import { useAuth } from '../../../../../../../hooks/useAuth'
import { patchWebsite } from '../../../../../../../api/society-websites'

interface SocietyWebsiteQuestionsAndAnswersItemContentProps {
  website: ISocietyWebsiteModel
  questionId: string
  sectionId: string
  content: string | undefined
}

export const SocietyWebsiteQuestionsAndAnswersItemContent = ({
  website,
  questionId,
  sectionId,
  content,
}: SocietyWebsiteQuestionsAndAnswersItemContentProps): React.ReactNode => {
  const { userId } = useAuth()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()
  const {
    show: showEditModal,
    open: openEditModal,
    close: closeEditModal,
  } = useModal()
  const {
    show: showConfirmationModal,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useModal()

  const isAdmin = isUserAdminInSociety(society, userId as string)

  const { mutate: mutatePatchWebsite } = useMutation({
    mutationFn: patchWebsite,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate(
          'createUpdateQuestionsAndAnswers.flashMessage.deleteQuestionSuccess'
        )
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate(
          'createUpdateQuestionsAndAnswers.flashMessage.deleteQuestionFailure'
        )
      )
    },
  })

  const removeQuestion = async (): Promise<void> => {
    const data: NSocietiesWebsites.NPatch.IRequestBody = {
      brokerQuestion: {
        ...website.brokerQuestion,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        sections: [
          ...(website.brokerQuestion?.sections
            ? website.brokerQuestion.sections.map((_section) => ({
                ..._section,
                questions: _section.questions?.filter(
                  (_question) => _question._id !== questionId
                ),
              }))
            : []),
        ],
      },
    }

    mutatePatchWebsite({ id: website._id, body: data })
  }

  return (
    <>
      <div className="flex flex-col py-4">
        <div>{content}</div>
        {isAdmin && (
          <div className="mt-6 flex flex-row space-x-3">
            <Button
              onClick={openEditModal}
              label={translate('common.actions.edit')}
            />
            <Button
              variant={ButtonVariant.DANGER}
              onClick={openConfirmationModal}
              label={translate('common.actions.delete')}
            />
          </div>
        )}
      </div>
      <CreateUpdateSocietyWebsiteFAQItemModal
        website={website}
        questionId={questionId}
        sectionId={sectionId}
        show={showEditModal}
        close={closeEditModal}
      />
      <ConfirmationModal
        title={translate(
          'createUpdateQuestionsAndAnswers.confirmationModal.title'
        )}
        description={translate(
          'createUpdateQuestionsAndAnswers.confirmationModal.description'
        )}
        show={showConfirmationModal}
        close={closeConfirmationModal}
        onConfirm={removeQuestion}
        deleteMode
      />
    </>
  )
}
